import { useTranslation } from 'react-i18next';
import type { SubscriptionEvent } from '@/services/adminMetricsService';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/formatDate';

interface EventsFeedProps {
  events: SubscriptionEvent[];
  formatCents: (cents: number, currency: string) => string;
}

/**
 * Recent subscription events feed.
 * MRR delta coloured by sign (positive = success, negative/zero = muted).
 * Date shown as relative ISO string (no external dependency needed).
 */
export function EventsFeed({ events, formatCents }: EventsFeedProps) {
  const { t } = useTranslation();

  if (events.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('admin.metrics.events.empty')}</p>;
  }

  return (
    <ul className="space-y-2">
      {events.map((ev) => {
        const mrrPositive = ev.mrr_cents > 0;
        const mrrZero = ev.mrr_cents === 0;
        return (
          <li
            key={ev.id}
            className="flex items-start justify-between gap-3 py-2 border-b border-border last:border-0"
          >
            <div className="flex flex-col gap-0.5 min-w-0">
              <span className="text-sm font-medium truncate">
                {t(`admin.metrics.events.type_${ev.event_type}`, { defaultValue: ev.event_type })}
              </span>
              <span className="text-xs text-muted-foreground">
                {ev.tier ?? '—'} · {ev.to_status} · tenant #{ev.tenant_id}
              </span>
            </div>
            <div className="flex flex-col items-end gap-0.5 shrink-0">
              {!mrrZero ? (
                <span
                  className={cn(
                    'text-sm font-semibold tabular-nums',
                    mrrPositive ? 'text-success' : 'text-destructive'
                  )}
                >
                  {mrrPositive ? '+' : ''}
                  {formatCents(ev.mrr_cents, ev.currency)}
                </span>
              ) : (
                <span className="text-sm text-muted-foreground">—</span>
              )}
              <time
                dateTime={ev.occurred_at}
                className="text-xs text-muted-foreground"
                title={ev.occurred_at}
              >
                {formatDate(ev.occurred_at)}
              </time>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
